import { z } from 'zod';
import { get } from '../http';
import { type ListQuery, paginated, resource } from '../schemas/common';
import {
  achievementRewardSchema,
  alphaClubTierSchema,
  clubBProgressRowSchema,
  eliteRankEnrollmentSchema,
  type IncomeType,
  incomeRecordSchema,
  incomeSummarySchema,
  memberRewardTrackerSchema,
} from '../schemas/domain';

export interface IncomeListQuery extends ListQuery {
  type?: IncomeType;
  level?: number;
}

export const incomeApi = {
  summary: () => get('/income/summary', resource(incomeSummarySchema)),

  /** Generic ledger — one page per income type in the UI. */
  list: (type: IncomeType, query: IncomeListQuery = {}) =>
    get(`/income/${type.replace(/_/g, '-')}`, paginated(incomeRecordSchema), query),

  all: (query: IncomeListQuery = {}) => get('/income', paginated(incomeRecordSchema), query),

  /** Alpha Club A tier progress (ALPHA 1..7). */
  alphaClubAProgress: () => get('/income/alpha-club-a/progress', resource(z.array(alphaClubTierSchema))),

  /** Alpha Club B tier progress (B1..B7). */
  alphaClubBProgress: () => get('/income/alpha-club-b/progress', resource(z.array(alphaClubTierSchema))),

  /** Elite Rank recurring payout schedule — Silver/Gold/Diamond. */
  eliteRankEnrollment: () =>
    get('/income/elite-rank/enrollment', resource(eliteRankEnrollmentSchema.nullable())),

  /** Binary matched left/right milestones. */
  achievementRewards: () =>
    get('/income/achievement-rewards', resource(z.array(achievementRewardSchema))),

  /** Level Income breakdown across the 7 levels. */
  levelBreakdown: () =>
    get(
      '/income/level-income/breakdown',
      resource(
        z.array(
          z.object({
            level: z.number(),
            rate_usd: z.string(),
            rate_lds: z.string(),
            members: z.number(),
            active_members: z.number(),
            earned_usd: z.string(),
            earned_lds: z.string(),
          }),
        ),
      ),
    ),

  /* admin — per-type ledger browser across all members */
  adminList: (type: IncomeType | 'all', query: IncomeListQuery = {}) =>
    get(`/admin/income/${type === 'all' ? 'all' : type.replace(/_/g, '-')}`, paginated(incomeRecordSchema), query),

  /**
   * Admin — cross-member progress toward Club B tiers, closest-to-qualifying
   * first. Only members with qualifying_count > 0 are included.
   */
  adminAlphaClubBProgress: () =>
    get('/admin/income/alpha-club-b/progress', resource(z.array(clubBProgressRowSchema))),

  /**
   * Admin — one member's Achievement Reward tracker (the same view the member
   * sees in their own panel, plus the Active/Inactive leg breakdown). `id` is
   * a user_code (numeric id also accepted as a fallback — see
   * MemberController::rewardTracker()).
   */
  adminRewardTracker: (id: string) =>
    get(`/admin/members/${id}/reward-tracker`, resource(memberRewardTrackerSchema)),
};
